import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { HERO_IMG, STEPS, FEATURES, TESTIMONIALS, TIERS } from "./LandingData";

export function Nav() {
  return (
    <header className="border-b-2 border-black bg-white sticky top-0 z-40">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <div className="w-9 h-9 bg-[#FF4F00] border-2 border-black flex items-center justify-center font-display font-black text-white">V</div>
          <span className="font-display font-black text-2xl tracking-tight">VIBELEAD</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-bold">
          <a href="#problem" className="hover:text-[#FF4F00]" data-testid="nav-problem">PROBLEM</a>
          <a href="#how-it-works" className="hover:text-[#FF4F00]" data-testid="nav-how">HOW IT WORKS</a>
          <a href="#features" className="hover:text-[#FF4F00]" data-testid="nav-features">FEATURES</a>
          <a href="#pricing" className="hover:text-[#FF4F00]" data-testid="nav-pricing">PRICING</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="btn-brutal-outline !px-4 !py-2 text-sm" data-testid="nav-login">LOG IN</Link>
          <Link to="/signup" className="btn-brutal !px-4 !py-2 text-sm" data-testid="nav-signup">START FREE</Link>
        </div>
      </div>
    </header>
  );
}

export function Hero() {
  return (
    <section className="border-b-2 border-black relative overflow-hidden">
      <div className="dot-grid absolute inset-0 opacity-60" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center relative">
        <div className="md:col-span-7">
          <div className="inline-flex items-center gap-2 border-2 border-black px-3 py-1 bg-white shadow-brutal mb-6">
            <span className="w-2 h-2 bg-[#FF4F00]" />
            <span className="label-cap !text-black">Vibe-coded · Built for serious creators</span>
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-balance" data-testid="hero-headline">
            Your list isn't growing<br />
            because your tools are<br />
            <span className="bg-[#FF4F00] text-white px-2">Garbage.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-zinc-900 max-w-xl leading-relaxed font-bold" data-testid="hero-subheadline">
            Beautiful lead capture pages. Instant content delivery. Built for people who are done playing small.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/signup" className="btn-brutal text-base !px-6 !py-4" data-testid="hero-cta-primary">
              Start Building Free <ArrowRight size={18} />
            </Link>
            <a href="#pricing" className="btn-brutal-outline text-base !px-6 !py-4" data-testid="hero-cta-secondary">
              See Pricing
            </a>
          </div>
          <p className="mt-4 text-sm text-zinc-600 italic">Takes 4 minutes. No tech degree required.</p>
        </div>
        <div className="md:col-span-5">
          <div className="relative">
            <img src={HERO_IMG} alt="Solo creator at home desk" className="w-full aspect-[4/5] object-cover border-2 border-black shadow-brutal-lg" data-testid="hero-image" />
            <div className="absolute -bottom-6 -left-6 bg-white border-2 border-black p-4 shadow-brutal max-w-[230px]">
              <div className="label-cap mb-1">Last 24h</div>
              <div className="font-display font-black text-3xl">+1,284</div>
              <div className="text-xs text-zinc-600">leads captured by VibeLead operators</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Marquee() {
  const words = ["CAPTURE", "DELIVER", "GROW", "CONVERT", "GET PAID", "OWN YOUR LIST", "DONE PLAYING SMALL"];
  return (
    <section className="border-b-2 border-black bg-black text-white overflow-hidden py-5">
      <div className="flex marquee whitespace-nowrap">
        {[...Array(2)].flatMap((_, i) =>
          words.map((t, j) => (
            <span key={`${i}-${j}`} className="font-display font-black text-2xl tracking-tight px-8 inline-flex items-center">
              {t} <span className="ml-8 text-[#FF4F00]">●</span>
            </span>
          ))
        )}
      </div>
    </section>
  );
}

export function Problem() {
  return (
    <section id="problem" className="border-b-2 border-black">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20">
        <div className="label-cap mb-4">// The problem</div>
        <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-balance mb-10" data-testid="problem-headline">
          You're bleeding leads<br />every single day.
        </h2>
        <div className="space-y-5 text-lg md:text-xl text-zinc-800 leading-relaxed max-w-3xl">
          <p>You know what's happening right now?</p>
          <p>Someone found your content.</p>
          <p>They were interested.</p>
          <p>They clicked.</p>
          <p>And they landed on something that looked like you didn't care — a clunky opt-in form bolted onto a page that screamed <em className="font-bold not-italic bg-[#FFF4EE] px-1">amateur</em> — and they left.</p>
          <p className="font-display font-black text-2xl md:text-3xl text-black">No name. No email. No chance.</p>
          <p className="font-display font-black text-2xl md:text-3xl text-[#FF4F00]">Gone.</p>
          <div className="border-l-4 border-black pl-5 my-8 space-y-3">
            <p>That's not a traffic problem.</p>
            <p>That's a first impression problem.</p>
          </div>
          <p>Every ugly page is a door that stays closed.</p>
          <p>Every broken delivery is a subscriber who never opens your email again.</p>
          <p>Every free resource dumped in a generic autoresponder is a value signal that says: <em className="font-bold not-italic">"I don't take this seriously."</em></p>
          <p className="font-display font-black text-2xl text-black mt-8">The market doesn't reward effort. It rewards <span className="text-[#FF4F00]">perception.</span></p>
          <p>And right now — your perception has a leak in it.</p>
        </div>
      </div>
    </section>
  );
}

export function Agitation() {
  return (
    <section className="border-b-2 border-black bg-black text-white">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20">
        <div className="label-cap !text-zinc-500 mb-4">// Here's the part nobody talks about</div>
        <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-balance mb-10" data-testid="agitation-headline">
          The container is broken.
        </h2>
        <div className="space-y-5 text-lg md:text-xl text-zinc-200 leading-relaxed max-w-3xl">
          <p>You can have the best lead magnet ever written.</p>
          <p>The most valuable PDF. The most dialed-in YouTube training. The most detailed Google Doc.</p>
          <p className="font-display font-black text-3xl text-white">Doesn't matter.</p>
          <p>If the page that collects the lead looks like it was built during a power outage — they bounce.</p>
          <p>If the delivery experience feels like a broken vending machine — they unsubscribe.</p>
          <p>If your opt-in page has no pulse, no personality, no <em className="font-bold not-italic text-[#FF4F00]">vibe</em> — you're invisible.</p>
          <div className="border-l-4 border-[#FF4F00] pl-5 my-8 space-y-3">
            <p>You've been told to focus on traffic.</p>
            <p>Content. Consistency. Posting every day.</p>
            <p className="font-bold text-white">Nobody told you the container was broken.</p>
          </div>
          <p className="font-display font-black text-3xl text-[#FF4F00]">Now you know.</p>
        </div>
      </div>
    </section>
  );
}

export function SolutionIntro() {
  return (
    <section className="border-b-2 border-black">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 text-center">
        <div className="label-cap mb-4">// The solution</div>
        <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-balance mb-8 max-w-3xl mx-auto" data-testid="solution-headline">
          VibeLead fixes the container.
        </h2>
        <p className="text-lg md:text-xl text-zinc-700 max-w-3xl mx-auto leading-relaxed">
          VibeLead is a vibe-coded lead capture and content delivery platform built for creators, marketers, and offer builders who are serious about growing a list that actually converts.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
          <div className="border-2 border-black p-5 bg-zinc-50">
            <div className="text-zinc-500 line-through font-bold mb-1">A template you have to fight with.</div>
            <div className="text-zinc-500 line-through font-bold">A page builder with 400 settings and a 3-hour learning curve.</div>
          </div>
          <div className="border-2 border-black p-5 bg-[#FF4F00] text-white">
            <div className="font-display font-black text-xl">One platform.</div>
            <div className="font-display font-black text-xl">Lead page. Content delivery. List growth.</div>
            <div className="font-display font-black text-xl">Done.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  return (
    <section id="how-it-works" className="border-b-2 border-black bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="max-w-3xl mb-12">
          <div className="label-cap mb-4">// How it works</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-balance">
            Live page in five steps.
          </h2>
          <p className="mt-4 text-lg text-zinc-700 max-w-2xl">No design skills. No code. Pick → name it → customize → publish → deliver. The walkthrough below is the actual product.</p>
        </div>
        <div className="space-y-12">
          {STEPS.map((s, i) => (
            <div
              key={s.title}
              data-testid={`how-step-${i + 1}`}
              className={`grid md:grid-cols-12 gap-8 items-center ${i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""}`}
            >
              <div className="md:col-span-5">
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-display font-black text-6xl text-[#FF4F00] leading-none">0{i + 1}</span>
                  <span className="label-cap">Step</span>
                </div>
                <h3 className="font-display font-black text-3xl md:text-4xl tracking-tight leading-tight mb-3">{s.title}</h3>
                <p className="text-base md:text-lg text-zinc-700 leading-relaxed">{s.desc}</p>
              </div>
              <div className="md:col-span-7">
                <div className="border-2 border-black shadow-brutal-lg overflow-hidden bg-zinc-100">
                  <img src={s.img} alt={s.title} className="w-full h-auto block" loading="lazy" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-14 text-center">
          <Link to="/signup" className="btn-brutal text-base !px-6 !py-4" data-testid="how-cta">
            Start Building Free <ArrowRight size={18} />
          </Link>
          <p className="mt-3 text-sm text-zinc-600 italic">Takes 4 minutes. No tech degree required.</p>
        </div>
      </div>
    </section>
  );
}

export function Features() {
  return (
    <section id="features" className="border-b-2 border-black">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="max-w-3xl mb-12">
          <div className="label-cap mb-4">// What you actually get</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-balance">
            Identity, not specs.
          </h2>
        </div>
        <div className="space-y-6">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} data-testid={f.testid} className="border-2 border-black bg-white grid md:grid-cols-12 gap-0 hover:shadow-brutal transition-shadow">
                <div className="md:col-span-2 bg-black text-white p-6 flex md:flex-col items-center md:items-start justify-between md:justify-center gap-3 border-b-2 md:border-b-0 md:border-r-2 border-black">
                  <span className="font-display font-black text-3xl text-[#FF4F00]">{f.badge}</span>
                  <Icon size={28} />
                </div>
                <div className="md:col-span-10 p-6 md:p-8">
                  <h3 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-3 leading-tight">{f.title}</h3>
                  <p className="text-zinc-700 leading-relaxed text-base md:text-lg">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Audience() {
  const items = [
    "You create content and want to capture the audience it builds.",
    "You have a lead magnet — a PDF, a training, a resource — and no clean way to deliver it.",
    "You're driving traffic but your list isn't growing the way it should.",
    "You want to look like a professional operator the moment someone hits your page.",
    "You know the money is in the list — and right now, the list isn't growing fast enough.",
  ];
  return (
    <section className="border-b-2 border-black bg-zinc-50">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20">
        <div className="label-cap mb-4">// Who this is for</div>
        <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-balance mb-10 max-w-3xl" data-testid="audience-headline">
          You're already the person<br />this was built for.
        </h2>
        <ul className="space-y-4 text-lg md:text-xl text-zinc-800 leading-relaxed max-w-3xl">
          {items.map((item) => (
            <li key={item} className="flex items-start gap-4">
              <span className="w-3 h-3 bg-[#FF4F00] mt-2 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-10 font-display font-black text-3xl md:text-4xl text-black">VibeLead is your fix.</p>
      </div>
    </section>
  );
}

export function SocialProof() {
  return (
    <section className="border-b-2 border-black">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="max-w-3xl mb-10">
          <div className="label-cap mb-4">// Operators in the wild</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[0.95] text-balance">
            Real numbers. Real lists.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="card-brutal !p-6 flex flex-col" data-testid={`testimonial-${t.name}`}>
              <Quote size={20} className="text-[#FF4F00] mb-3" />
              <p className="text-base md:text-lg text-zinc-800 leading-relaxed flex-1">"{t.quote}"</p>
              <div className="mt-5 pt-4 border-t-2 border-black">
                <div className="font-display font-bold">{t.name}</div>
                <div className="text-sm text-zinc-600">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Pricing() {
  return (
    <section id="pricing" className="border-b-2 border-black bg-zinc-50">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="label-cap mb-4">// Pricing</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-balance mb-4" data-testid="pricing-headline">
            How serious are you<br />about your list?
          </h2>
          <p className="text-lg text-zinc-700 italic">Three paths. All of them say yes. The only question is which yes is yours.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((tier) => (
            <PricingCard key={tier.name} tier={tier} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingCard({ tier }) {
  return (
    <div
      data-testid={tier.testid}
      className={`card-brutal !p-7 flex flex-col bg-white ${tier.popular ? "border-[#FF4F00] !border-4" : ""}`}
    >
      {tier.flag && (
        <div className={`inline-flex self-start px-3 py-1 text-xs font-bold tracking-[0.18em] mb-4 border-2 border-black ${tier.popular ? "bg-[#FF4F00] text-white" : "bg-black text-white"}`}>
          {tier.flag}
        </div>
      )}
      <div className="font-display font-black text-2xl mb-1">{tier.name}</div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-display font-black text-5xl">{tier.price}</span>
        {tier.cadence && <span className="text-zinc-500 text-sm">{tier.cadence}</span>}
      </div>
      <p className="text-sm text-zinc-600 italic mb-5 leading-relaxed">{tier.pitch}</p>
      <ul className="space-y-2 text-sm mb-7 flex-1">
        {tier.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <span className="w-2 h-2 bg-[#FF4F00] mt-2 flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Link
        to={tier.href}
        className={tier.popular ? "btn-brutal w-full justify-center" : "btn-brutal-outline w-full justify-center"}
        data-testid={`${tier.testid}-cta`}
      >
        {tier.cta} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

export function ObjectionCrusher() {
  return (
    <section className="border-b-2 border-black">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20">
        <div className="label-cap mb-4">// Objection crusher</div>
        <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-balance mb-10" data-testid="objection-headline">
          "I already have <span className="bg-zinc-200 px-2">[tool]</span>."
        </h2>
        <div className="space-y-4 text-lg md:text-xl text-zinc-800 leading-relaxed max-w-3xl">
          <p>You might.</p>
          <p>But does it look this clean?</p>
          <p>Does it deliver content in every format your audience expects?</p>
          <p>Does it take 4 minutes to build a page — or 4 hours?</p>
          <p>Does it feel like it was built for <em className="font-bold not-italic">you</em>, or built for a dev team you don't have?</p>
          <div className="mt-8 border-l-4 border-[#FF4F00] pl-5 space-y-2">
            <p>VibeLead isn't competing with your tech stack.</p>
            <p className="font-display font-black text-2xl text-black">It's replacing the part of it that's been leaking leads since day one.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ClosingCta() {
  return (
    <section className="border-b-2 border-black bg-black text-white">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-24 text-center">
        <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-balance mb-10 max-w-4xl mx-auto" data-testid="closing-headline">
          The list you don't build today<br />is the business you won't have tomorrow.
        </h2>
        <div className="space-y-4 text-lg md:text-xl text-zinc-300 leading-relaxed max-w-3xl mx-auto text-left md:text-center mb-10">
          <p>Every day you drive traffic to a page that doesn't convert — you're funding your competitors' lists.</p>
          <p>Every lead magnet you can't deliver cleanly is a subscriber who assumes you're an amateur.</p>
          <p>Every week without a real lead capture system is a week your audience is building someone else's empire.</p>
          <p className="font-bold text-white pt-3">You already know what you need to do.</p>
          <p>The only question is whether you do it today — or explain to yourself again why next week is better.</p>
          <p className="font-display font-black text-3xl text-[#FF4F00] !mt-8">There is no next week.</p>
          <p>There is only the page you build right now — and the list it starts filling tonight.</p>
        </div>
        <Link to="/signup" className="btn-brutal !bg-[#FF4F00] !text-white text-base !px-7 !py-5 inline-flex" data-testid="closing-cta">
          Build Your First VibeLead Page — It's Free <ArrowRight size={18} />
        </Link>
        <p className="mt-5 text-sm text-zinc-400 italic">No credit card. No friction. No excuse.</p>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row justify-between gap-6">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#FF4F00] border-2 border-black flex items-center justify-center font-display font-black text-white">V</div>
            <span className="font-display font-black text-2xl tracking-tight">VIBELEAD</span>
          </div>
          <p className="text-sm text-zinc-600 max-w-xs italic">Capture. Deliver. Grow. Built for creators who take their list seriously.</p>
        </div>
        <div className="text-sm text-zinc-500">© {new Date().getFullYear()} VibeLead. All rights reserved.</div>
      </div>
    </footer>
  );
}
